/**
 * @module TeamDashboardTeamSelector
 */
/**
 * @Author: Anandp
 * @Date:   2024-12-10 15:13:21
 * @Last Modified by:   Eswari
 * @Last Modified time: 2026-01-28
 */
import * as React from "react";
import {
  Box,
  Drawer,
  List,
  Checkbox,
  Typography,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  OutlinedInput,
  InputAdornment,
  SvgIcon,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { Scrollbar } from "src/components/scrollbar";
import { createTypography } from "src/theme/base/customTypography";
import { useTeam } from "src/contexts/team-context";
import { useSidebar } from 'src/contexts/SidebarContext'; // Import the context hook
import { useState } from "react";

const SIDE_NAV_WIDTH = 210;
const SIDE_NAV_MINIMIZED_WIDTH = 50; 

export default function TemporaryDrawer(props) {
  const setDrawerOpen = props.setDrawerOpen;
  const { isMinimized } = useSidebar();
  const typography = createTypography();
  const {
    selectedTeamId,
    setTeamId,
    setTeamName,
    setToClickWithOrg,
    clickWithOrg,
    clickWithPage,
    seteamChange,
    setIsLoadingTeam,
    teamsList,
    noTeam,
  } = useTeam();

  const [searchData, setSearchData] = useState("");
  
  /* ------------------ Memoized Search ------------------ 
  * Memoizes the filtered teams list based on the search data.
  * @function filteredTeamsList
  * @returns {Array} - The filtered list of teams.
  */
  const filteredTeamsList = React.useMemo(() => {
    if (!searchData) return teamsList;
    return teamsList.filter((team) =>
      team.teamName.toLowerCase().includes(searchData.toLowerCase())
    );
  }, [searchData, teamsList]);

  /* ------------------ Team Selection ------------------ 
  * Handles the change event for selecting a team from the drawer and updates the state accordingly.
  *
  * @function handleTeamChange
  * @param {Object} event - The event object triggered by the change action.
  * @param {number} teamId - The ID of the selected team.
  * @param {string} teamName - The name of the selected team.
  * @returns {void}
  *
  * @description
  * This function is responsible for handling the change event when a team is selected from the drawer.
  * It updates the following states:
  * - `selectedTeamId` with the provided `teamId`.
  * - `teamName` with the provided `teamName`.
  * - It also updates sessionStorage with the selected team’s ID and name for persistence across sessions.
  * - Additionally, the function resets certain state values like `toClickWithOrg` and triggers navigation to the team diversity page.  
  * @example
  * // Handle team selection change
  * handleTeamChange(event, 1, "Team A");
  */
  const handleTeamChange = (_, teamId, teamName) => {
    setIsLoadingTeam(true);
    setToClickWithOrg(false);
    setTeamId(teamId);
    setTeamName(teamName);
    // Properly close the MUI Drawer by updating the state object
    setState({ left: false });
    // Also signal the parent SideNavItem to unmount this drawer
    setDrawerOpen(false);

    sessionStorage.setItem("teamDashboardTeamId", teamId);
    sessionStorage.setItem("teamDashboardTeamName", teamName);

    // Do NOT navigate here — updatePermission in team-context will navigate
    // once permissions are resolved, preventing a double-render / double API call.
    seteamChange(true);
  };

  const [state, setState] = React.useState({
    left: false,
  });

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    // Check if the click event originated from within a checkbox
    if (event.target.tagName === "INPUT" && event.target.type === "checkbox") {
      return;
    }

    setState({ ...state, [anchor]: open });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const list = (anchor) => (
    <Box
      sx={{
        width: anchor === "top" || anchor === "bottom" ? "auto" : {lg:'310px', md:'310px', sm:'280px', xs:'280px'},
        height: "100%",
        backgroundColor: "#583F99",
        borderRadius: "0 15px 15px 0",
        zIndex: 1201,
        paddingLeft: {lg:'45px',md:'45px',sm:'5px',xs:'5px'},
        paddingTop: "25px",
       
      }}
      role="presentation"
      onClick={(event) => {
        if (event.target !== event.currentTarget) return; // Don't close on input events
        toggleDrawer(anchor, false);
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return; // Don't close on input events
        toggleDrawer(anchor, false);
      }}
      p={2}
    >
      <Typography
        sx={{ ...typography.subtitle.regularColorless, color: "white" }}
      >
        Team Selector
      </Typography>
      <OutlinedInput
          value={searchData}
          autoFocus
          fullWidth
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiInputBase-input': { padding: '8px 0!important' },
            '&.MuiInputBase-root': { backgroundColor: '#fff!important' },
            '& .MuiInputAdornment-root path': { fill: '#535B66' },
            '& .MuiSvgIcon-root': { width: '16px', height: '16px' },
            ' input::placeholder': { color: '#535B66', fontSize: '12px' },
            

          }}
          placeholder="Search"
          onChange={(e) => setSearchData(e.target.value)}
          startAdornment={
            <InputAdornment position="start"
            >
              <SvgIcon>
                <SearchIcon fontSize="small" />
              </SvgIcon>
            </InputAdornment>
          }
        />
      <Scrollbar style={{ maxHeight: "100%", width: "100%" }}>
      {!clickWithOrg && !selectedTeamId && noTeam && (
          <List>
            <div>
              <Accordion sx={{ backgroundColor: "transparent" }}>
                <AccordionSummary>
                  <Typography
                    sx={{
                      ...typography.subtitle.regularColorless,
                      color: "white",
                    }}
                  >
                    No team to list down
                  </Typography>
                </AccordionSummary>
              </Accordion>
            </div>
          </List>
        )}
        {clickWithOrg && clickWithPage && Array.isArray(filteredTeamsList) && (
          <List>
          {filteredTeamsList.map((team, index) => (
            <div key={index}>
              <Accordion
               sx={{ backgroundColor: "transparent",
                
                }}>
                <AccordionSummary
                
                  // expandIcon={<PlusBUtton />}
                  aria-controls={`panel${index + 1}-content`}
                  id={`panel${index + 1}-header`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <FormControlLabel

                    label={
                      <Typography
                        sx={{
                          ...typography.subtitle.regularColorless,
                          color: "white",
                        }}
                      >
                        {team.teamName}
                      </Typography>
                    }
                    control={
                      <Checkbox
                        checked={selectedTeamId === team.teamId}
                        onChange={(event) =>
                          handleTeamChange(event, team.teamId, team.teamName)
                        }
                      />
                    }
                  />
                </AccordionSummary>
                {/* <AccordionDetails>{children}</AccordionDetails> */}
              </Accordion>
            </div>
          ))}
          </List>
        )}
        {/* {!clickWithOrg && selectedTeamId && Array.isArray(filteredTeamsList) && ( */}
        {!clickWithOrg && Array.isArray(filteredTeamsList) && (
        <List>
          {filteredTeamsList.map((team, index) => (
            <div key={index}>
              <Accordion sx={{ backgroundColor: "transparent" }}>
                <AccordionSummary
                  // expandIcon={<PlusBUtton />}
                  aria-controls={`panel${index + 1}-content`}
                  id={`panel${index + 1}-header`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <FormControlLabel
                    label={
                      <Typography
                        sx={{
                          ...typography.subtitle.regularColorless,
                          color: "white",
                        }}
                      >
                        {team.teamName}
                      </Typography>
                    }
                    control={
                      <Checkbox
                        checked={selectedTeamId === team.teamId}
                        onChange={(event) =>
                          handleTeamChange(event, team.teamId, team.teamName)
                        }
                      />
                    }
                  />
                </AccordionSummary>
                {/* <AccordionDetails>{children}</AccordionDetails> */}
              </Accordion>
            </div>
          ))}
        </List>
        )}
      </Scrollbar>
    </Box>
  );

  React.useEffect(() => {
    // Open the drawer directly when the component mounts
    setState((prev) => ({ ...prev, left: true }));
  }, []);


  // if(noTeam) {
  //    return (
  //     <div
  //       style={{
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         height: "100vh", // Adjust this based on your layout
  //       }}
  //     >
  //       <SldButtons.BlackButtonCap variant="contained">
  //         <Typography textAlign="center">
  //           User <br />
  //           Not Yet Assigned to Team
  //         </Typography>
  //       </SldButtons.BlackButtonCap>
  //     </div>
  //   )
  // }

  return (
    <Drawer
      anchor="left"
      open={state["left"]}
      onClose={() => {
        setDrawerOpen(false);
        toggleDrawer("left", false);
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0)!important", // Set the background color of the backdrop to transparent
        },
       '& .MuiPaper-root.MuiPaper-elevation': {
          left: {
            lg: isMinimized ? SIDE_NAV_MINIMIZED_WIDTH : SIDE_NAV_WIDTH,
            md: isMinimized ? SIDE_NAV_MINIMIZED_WIDTH : SIDE_NAV_WIDTH,
            sm: 0,
            xs: 0,
          },
          top:{lg:'0',md:'0', sm:'155px', xs:'155px'},
          zIndex: -1, overflow: 'hidden',
          height:{lg:'100%', md:'100%', sm:'70%', xs:'calc(100vh - 200px)'}
        },

        '& .MuiPaper-root.MuiPaper-elevation .MuiPaper-root.MuiPaper-elevation': { left: 0, zIndex: 1,top:{lg:' ',md:'', sm:'0', xs:'0'},
        height:'100%' },
        "& .MuiButtonBase-root": {
          "& rect": { fill: "#fff", stroke: "#fff;" },
        },
        "& .MuiAccordionDetails-root .MuiBox-root": { marginLeft: 0 },
        "& .MuiAccordionSummary-root": {
          borderBottom: "1px solid rgba(255,255,255,.3)",
          borderTop: "1px solid rgba(255,255,255,.3)",
        },
        "& .MuiAccordionSummary-content": { margin: "2px 0!important" },
        "& .MuiAccordionSummary-root.Mui-expanded": {
          minHeight: "25px",
          margin: "2px 0",
        },
        "& .MuiButtonBase-root.MuiCheckbox-root": { padding: "4px" },
        "& .MuiFormControlLabel-root .MuiTypography-root": { paddingLeft: 0 },
        '& .MuiButtonBase-root.Mui-checked':{color:'#fff!important'}

      }}
    >
      {list("left")}

    </Drawer>
  );
}
